// Приёмка без интернета (02.10.2026): сканы и «Подтвердить приёмку» копятся на устройстве и уходят на сервер
// строго по порядку, когда связь возвращается. У каждого скана свой ключ — повторная отправка не задваивает приход.
import { api, ApiError, authStore } from '../../api/client.ts';
import { kvDel, kvGet, kvKeys, kvSet } from './store.ts';

export interface RcItem {
  key: string; receipt_id: number; kind: 'scan' | 'post'; at: string; seq: number;
  body: any;                       // тело запроса как есть
  view?: { sku: string; name: string; unit: string; qty: number; damaged: boolean };   // для показа на экране
  error?: string | null;
}
const KEY = 'rcq:';
let cache: RcItem[] = [];
let loaded = false;
let snapshot: RcItem[] = [];
const listeners = new Set<() => void>();
const publish = () => { snapshot = [...cache].sort((a, b) => a.seq - b.seq); listeners.forEach((l) => l()); };

export const rcQueue = {
  subscribe(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); },
  all: () => snapshot,
};
async function load() {
  if (loaded) return;
  loaded = true;
  cache = (await Promise.all((await kvKeys(KEY)).map((k) => kvGet<RcItem>(k)))).filter(Boolean) as RcItem[];
  publish();
}
export async function rcInit() { await load(); }
export async function rcAdd(item: Omit<RcItem, 'at' | 'seq'>) {
  if (authStore.get().user?.role === 'viewer') throw new ApiError(403, {error:'Роль «Просмотр»: изменение данных запрещено',code:'forbidden'});
  await load();
  const next: RcItem = { ...item, at: new Date().toISOString(), seq: Date.now() * 1000 + (cache.length % 1000) };
  cache = [...cache, next];
  await kvSet(KEY + next.key, next);
  publish();
}
export async function rcRemove(key: string) {
  await load();
  cache = cache.filter((i) => i.key !== key);
  await kvDel(KEY + key);
  publish();
}
export const rcHas = (receiptId: number) => cache.some((i) => i.receipt_id === receiptId);

export type RcResult = { item: RcItem; ok: true; res: any } | { item: RcItem; ok: false; error: string; code?: string };
let syncing: Promise<void> | null = null;
/** Отправить очередь по порядку. Нет связи — останавливаемся; сервер отказал — строка остаётся с причиной */
export function rcSync(onResult?: (r: RcResult) => void): Promise<void> {
  syncing ??= (async () => {
    await load();
    const blocked = new Set<number>();   // в приёмке есть неотправленная ошибка — дальше по ней не идём, порядок важен
    for (const it of [...cache].sort((a, b) => a.seq - b.seq)) {
      if (it.error) { blocked.add(it.receipt_id); continue; }
      if (blocked.has(it.receipt_id)) continue;
      try {
        const res = await api<any>(`/receipts/${it.receipt_id}/${it.kind}`, { body: it.body });
        await rcRemove(it.key);
        onResult?.({ item: it, ok: true, res });
      } catch (e: any) {
        if (e instanceof ApiError && (e.status === 0 || e.status >= 502)) return;   // связи всё ещё нет
        const error = String(e?.message ?? e);
        if (it.kind === 'post') await rcRemove(it.key);   // подтверждение не повторяем само: человек откроет приёмку и решит
        else { const cur = cache.find((x) => x.key === it.key); if (cur) { cur.error = error; await kvSet(KEY + it.key, cur); publish(); } blocked.add(it.receipt_id); }
        onResult?.({ item: it, ok: false, error, code: e?.code });
      }
    }
  })().finally(() => { syncing = null; });
  return syncing;
}
